import { AxiosError, type AxiosResponse, HttpStatusCode } from 'axios';
import { client } from '@/api/http';
import { getIdFromUrl } from '@/api/getIdFromUrl';
import type { ImageModality } from '@/api/study/study';
import type { ApiCaseStudy } from '@/api/caseSettings';

export type CaseScanSeriesMessage = {
    /** the level message as a simple string (e.g. 'info', 'warn', 'debug') */
    level: string;

    /** the text message with parameters replaced */
    message: string;
};

type CaseScanFile = {
    /** The original filename */
    name: string;

    /** an optional set of messages */
    messages?: CaseScanSeriesMessage[];
};

export type ApiCaseStudyCreateData = ApiCaseStudy & {

    source_modality: ImageModality;

    /**
     * Series messages. An optional set of messages that describe issues detected in the series during
     * processing by the client.
     *
     * Notes:
     *   <ul>
     *      <li>the messages for each file are stored on the individual file resources</li>
     *      <li>the messages for each files not uploaded are included below for completeness/diagnostics/audit</li>
     *   </ul>
     */
    readonly messages?: CaseScanSeriesMessage[];

    /**
     * The messages for file that were excluded from upload because they were deemed to not be required
     * to perform the pre-surgical planning. It is expected that additional files will come from the PACS
     * system.
     */
    readonly excluded_files?: CaseScanFile[];

    /**
     * File information which at the moment is only used for logging information
     */
    readonly file_messages?: CaseScanFile[];

    readonly web_component_version: string;
};

export async function createStudy(caseId: number, body: ApiCaseStudyCreateData): Promise<string> {
    const { status, data, headers }: AxiosResponse = await client.post(
        `/cases/${caseId}/studies`,
        body,
    );
    if (status === HttpStatusCode.Created) {
        return data?.id ?? getIdFromUrl(headers.location);
    }

    throw new AxiosError(`Failed to create study: ${data?.detail}`);
}

export async function startStudyProcess(
    caseId: number,
    studyId: number | string,
    webComponentVersion: string,
) {
    const { status, data }: AxiosResponse = await client.post(
        `/cases/${caseId}/studies/${studyId}/process`,
        { web_component_version: webComponentVersion },
        /**
         * This request might take a while to complete in the server side, so we need to increase the timeout
         * The backend publishes a job to the queue and waits until the message was acknowledged by the queue, but
         * during period of high load this takes a while.
         */
        { timeout: 60 * 1000 },
    );
    if (status === HttpStatusCode.Accepted) {
        return;
    }

    throw new AxiosError(`Failed to save case settings: ${data?.detail}`);
}